import { debounce } from '@mui/material'
import {
  CarrierType,
  CarrierVisitAreaPartialDto,
  CarrierVisitPartialWorkAreaDto,
  CarrierVisitStatus,
} from '@operations/app/api'
import { useOperationsStore } from '@operations/AppProvider'
import carrierVisitService from '@operations/services/carrierVisitService'
import { TomAutoComplete } from '@tom-ui/ui'
import { useAsyncFetch } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useCallback, useState } from 'react'
import { CarrierVisitOption } from '../atoms/CarrierVisitOption'

interface Props {
  label: string
  carrierType: CarrierType
  selectedId?: number | null
  initialFilterName?: string
  dataCy?: string
  onChange: (value: CarrierVisitAreaPartialDto | null | undefined) => void
  onLoadWorkAreas?: (craneWorkAreas: CarrierVisitPartialWorkAreaDto[]) => void
}
export const CarrierVisitAreaAutoComplete = observer(
  ({
    label,
    carrierType,
    selectedId,
    initialFilterName,
    dataCy,
    onChange,
    onLoadWorkAreas,
  }: Props) => {
    const [visits, setVisits] = useState<CarrierVisitAreaPartialDto[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const { appStore } = useOperationsStore()

    const fetchVisits = useCallback(
      async (filter?: string) => {
        const newVisits = await carrierVisitService.getCarrierVisitsByPartialName(
          filter,
          carrierType,
          [
            CarrierVisitStatus.Expected,
            CarrierVisitStatus.Arrived,
            CarrierVisitStatus.InOperation,
            CarrierVisitStatus.Completed,
          ],
        )

        setVisits(newVisits.filter(v => v.workAreas.length > 0))

        return newVisits
      },
      [carrierType],
    )

    useAsyncFetch(async () => {
      if (initialFilterName) {
        await appStore.triggerRequestWithoutLoader(async () => {
          const initialVisits = await fetchVisits(initialFilterName)
          if (onLoadWorkAreas)
            onLoadWorkAreas(initialVisits.find(x => x.id === selectedId)?.workAreas ?? [])
        })
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchVisits, onLoadWorkAreas])

    const filterChange = debounce(async (filter?: string) => {
      setIsLoading(true)

      await appStore.triggerRequestWithoutLoader(async () => {
        await fetchVisits(filter)
      })

      setIsLoading(false)
    }, 500)

    return (
      <TomAutoComplete
        enablePortal
        entityKey={'id'}
        defaultValue={visits.find(x => x.id === selectedId)}
        label={label}
        options={visits}
        loading={isLoading}
        onChangeEntity={entity => onChange(entity)}
        onChangeFilter={filter => filterChange(filter)}
        getOptionLabel={(option: CarrierVisitAreaPartialDto) => {
          let label = option.carrierName ?? ''
          if (option.vesselVisitNumber) label += ` - ${option.vesselVisitNumber}`

          return label
        }}
        renderOption={(props, option) => (
          <CarrierVisitOption
            key={`carrier-visit-${option.id}`}
            name={option.carrierName!}
            status={option.status}
            date={option.ata ?? option.eta}
            rotationNumber={option.vesselVisitNumber}
            renderProps={props}
          />
        )}
        dataCy={dataCy ?? 'visits-partial-autocomplete'}
      />
    )
  },
)
